import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { 
  Plus, Star, Target, TrendingUp, Award, Building2, User,
  CheckCircle, XCircle, Clock, ChevronDown, ChevronUp, BarChart3
} from "lucide-react";
import api from "../lib/api";
import { OverallTrendChart, ParamBreakdownChart } from "../components/PerformanceTrendChart";

const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December"
];
const PARAMS = ["productivity","quality","teamwork","communication","punctuality"];
const PARAM_LABELS = { productivity:"Productivity", quality:"Quality of Work", teamwork:"Teamwork", communication:"Communication", punctuality:"Punctuality" };
const RATING_LABELS = { 1:"Needs Improvement", 2:"Below Expectations", 3:"Meets Expectations", 4:"Exceeds Expectations", 5:"Outstanding" };

const RatingSelector = ({ param, value, onChange }) => (
  <div className="flex items-center justify-between gap-3 py-1.5">
    <span className="text-sm text-slate-600 w-36">{PARAM_LABELS[param]}</span>
    <div className="flex gap-1">
      {[1,2,3,4,5].map(n => (
        <button key={n} type="button" onClick={() => onChange(n)}
          className={`w-8 h-8 rounded-full text-xs font-semibold transition-all ${n <= value ? "bg-amber-400 text-white shadow-sm" : "bg-slate-100 text-slate-400 hover:bg-slate-200"}`}
          data-testid={`rating-${param}-${n}`}>
          {n}
        </button>
      ))}
    </div>
    <span className="text-[10px] text-slate-400 w-28 text-right">{RATING_LABELS[value]}</span>
  </div>
);

const RatingDisplay = ({ ratings }) => (
  <div className="space-y-1">
    {PARAMS.map(p => {
      const val = ratings?.[p] || 0;
      return (
        <div key={p} className="flex items-center gap-2">
          <span className="text-xs text-slate-500 w-28">{PARAM_LABELS[p]}</span>
          <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all" style={{ width: `${(val/5)*100}%`, backgroundColor: val >= 4 ? "#22c55e" : val >= 3 ? "#f59e0b" : "#ef4444" }} />
          </div>
          <span className="text-xs font-semibold text-slate-600 w-6 text-right">{val}</span>
        </div>
      );
    })}
  </div>
);

const StatusBadge = ({ status }) => {
  const map = {
    pending_review: { label: "Pending Review", cls: "bg-slate-100 text-slate-600 border-slate-200" },
    dept_head_reviewed: { label: "Awaiting HR Approval", cls: "bg-amber-50 text-amber-700 border-amber-200" },
    hr_approved: { label: "Approved", cls: "bg-green-50 text-green-700 border-green-200" },
    hr_rejected: { label: "Rejected", cls: "bg-red-50 text-red-700 border-red-200" },
  };
  const s = map[status] || { label: status, cls: "bg-slate-100 text-slate-600" };
  return <Badge variant="outline" className={`${s.cls} text-[10px]`} data-testid={`status-badge-${status}`}>{s.label}</Badge>;
};

const Performance = () => {
  const { currentBusiness, user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [hrDialogOpen, setHrDialogOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [hrComments, setHrComments] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [filterMonth, setFilterMonth] = useState("");
  const [filterYear, setFilterYear] = useState(String(new Date().getFullYear()));
  const [filterDept, setFilterDept] = useState("all");
  const [trendEmployee, setTrendEmployee] = useState(null);
  const [trendData, setTrendData] = useState(null);
  const [trendLoading, setTrendLoading] = useState(false);

  const now = new Date();
  const [formData, setFormData] = useState({
    employee_id: "",
    month: String(now.getMonth() + 1),
    year: String(now.getFullYear()),
    ratings: { productivity: 3, quality: 3, teamwork: 3, communication: 3, punctuality: 3 },
    goals: "", achievements: "", areas_of_improvement: "", comments: ""
  });

  const isHrAdmin = ["super_admin", "business_admin", "hr_manager"].includes(user?.role);

  useEffect(() => {
    if (currentBusiness?.id) fetchData();
  }, [currentBusiness?.id]);

  const fetchData = async () => {
    try {
      const [reviewsRes, empRes, deptRes] = await Promise.all([
        api.get(`/reviews?business_id=${currentBusiness.id}`),
        api.get(`/employees?business_id=${currentBusiness.id}`),
        api.get(`/departments?business_id=${currentBusiness.id}`),
      ]);
      setReviews(reviewsRes.data);
      setEmployees(empRes.data);
      setDepartments(deptRes.data);
    } catch { toast.error("Failed to fetch performance data"); }
    finally { setLoading(false); }
  };

  const fetchTrend = async (empId) => {
    setTrendLoading(true);
    try {
      const res = await api.get(`/reviews/trend/${empId}?business_id=${currentBusiness.id}`);
      setTrendData(res.data);
      setTrendEmployee(empId);
    } catch { toast.error("Failed to load trend data"); }
    finally { setTrendLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.employee_id) { toast.error("Select an employee"); return; }
    try {
      await api.post("/reviews", {
        employee_id: formData.employee_id,
        business_id: currentBusiness.id,
        month: parseInt(formData.month),
        year: parseInt(formData.year),
        ratings: formData.ratings,
        goals: formData.goals, achievements: formData.achievements,
        areas_of_improvement: formData.areas_of_improvement, comments: formData.comments
      });
      toast.success("Performance review created");
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create review"); }
  };

  const handleHrAction = async (action) => {
    if (!selectedReview) return;
    try {
      await api.put(`/reviews/${selectedReview.id}/hr-action`, { action, comments: hrComments });
      toast.success(`Review ${action}d`);
      setHrDialogOpen(false);
      setSelectedReview(null);
      setHrComments("");
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Action failed"); }
  };

  const resetForm = () => setFormData({
    employee_id: "", month: String(now.getMonth() + 1), year: String(now.getFullYear()),
    ratings: { productivity: 3, quality: 3, teamwork: 3, communication: 3, punctuality: 3 },
    goals: "", achievements: "", areas_of_improvement: "", comments: ""
  });

  const getEmpName = (id) => { const e = employees.find(x => x.id === id); return e ? `${e.first_name} ${e.last_name}` : "Unknown"; };
  const getDeptName = (id) => { const d = departments.find(x => x.id === id); return d ? d.name : ""; };
  const getEmpDept = (empId) => { const e = employees.find(x => x.id === empId); return e?.department_id || ""; };

  // Filter reviews
  const filtered = reviews.filter(r => {
    if (filterMonth && filterMonth !== "all_months" && r.month !== parseInt(filterMonth)) return false;
    if (filterYear && r.year !== parseInt(filterYear)) return false;
    if (filterDept !== "all") {
      const empDept = getEmpDept(r.employee_id);
      if (empDept !== filterDept) return false;
    }
    return true;
  });

  const pendingHrCount = reviews.filter(r => r.status === "dept_head_reviewed").length;
  const approvedCount = reviews.filter(r => r.status === "hr_approved").length;
  const avgRating = reviews.length > 0 ? (reviews.reduce((s, r) => s + (r.overall_rating || 0), 0) / reviews.length).toFixed(1) : "0.0";

  if (!currentBusiness) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
        <Building2 className="h-16 w-16 text-slate-300 mb-4" />
        <h2 className="text-2xl font-semibold text-slate-800 font-heading mb-2">No Business Selected</h2>
        <p className="text-slate-500">Select a business to manage performance reviews</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="performance-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 font-heading">Monthly Performance Reviews</h1>
          <p className="text-slate-500">Evaluate employees monthly — reviewed by Dept Heads, approved by HR</p>
        </div>
        {isHrAdmin && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="create-review-btn">
                <Plus className="h-4 w-4 mr-2" /> New Review
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading">Create Monthly Review</DialogTitle>
                <DialogDescription>Rate employee performance across multiple parameters</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-2 max-h-[65vh] overflow-y-auto pr-2">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Employee</Label>
                    <Select value={formData.employee_id} onValueChange={v => setFormData({...formData, employee_id: v})}>
                      <SelectTrigger className="input-field" data-testid="review-employee-select"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        {employees.filter(e => e.is_active).map(e => (
                          <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Month</Label>
                      <Select value={formData.month} onValueChange={v => setFormData({...formData, month: v})}>
                        <SelectTrigger className="input-field" data-testid="review-month-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {MONTHS.map((m, i) => <SelectItem key={`month-${m}`} value={String(i+1)}>{m}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Year</Label>
                      <Input value={formData.year} onChange={e => setFormData({...formData, year: e.target.value})}
                        type="number" className="input-field" data-testid="review-year-input" />
                    </div>
                  </div>
                </div>

                <div className="border rounded-lg p-3 bg-slate-50/50">
                  <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Ratings (1-5)</p>
                  {PARAMS.map(p => (
                    <RatingSelector key={p} param={p} value={formData.ratings[p]}
                      onChange={v => setFormData({...formData, ratings: {...formData.ratings, [p]: v}})} />
                  ))}
                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-200">
                    <span className="text-sm font-semibold text-slate-700">Overall</span>
                    <span className="text-lg font-bold text-primary">{(Object.values(formData.ratings).reduce((a,b)=>a+b,0)/5).toFixed(1)}</span>
                  </div>
                </div>

                <div className="space-y-1.5"><Label className="text-xs">Goals</Label>
                  <Textarea value={formData.goals} onChange={e => setFormData({...formData, goals: e.target.value})}
                    placeholder="Goals for next period..." className="input-field min-h-[50px]" data-testid="review-goals-input" /></div>
                <div className="space-y-1.5"><Label className="text-xs">Achievements</Label>
                  <Textarea value={formData.achievements} onChange={e => setFormData({...formData, achievements: e.target.value})}
                    placeholder="Key achievements..." className="input-field min-h-[50px]" data-testid="review-achievements-input" /></div>
                <div className="space-y-1.5"><Label className="text-xs">Areas of Improvement</Label>
                  <Textarea value={formData.areas_of_improvement} onChange={e => setFormData({...formData, areas_of_improvement: e.target.value})}
                    placeholder="Areas to improve..." className="input-field min-h-[50px]" data-testid="review-improvement-input" /></div>
                <div className="space-y-1.5"><Label className="text-xs">Comments</Label>
                  <Textarea value={formData.comments} onChange={e => setFormData({...formData, comments: e.target.value})}
                    placeholder="Additional comments..." className="input-field min-h-[50px]" data-testid="review-comments-input" /></div>

                <Button type="submit" className="w-full btn-primary" data-testid="save-review-btn">Create Review</Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="stat-card"><CardContent className="p-0">
          <div className="flex items-center gap-4"><div className="p-3 rounded-xl bg-primary/10"><Star className="h-6 w-6 text-primary" /></div>
            <div><p className="text-2xl font-bold text-slate-800">{reviews.length}</p><p className="text-sm text-slate-500">Total Reviews</p></div></div>
        </CardContent></Card>
        <Card className="stat-card"><CardContent className="p-0">
          <div className="flex items-center gap-4"><div className="p-3 rounded-xl bg-amber-100"><Award className="h-6 w-6 text-amber-700" /></div>
            <div><p className="text-2xl font-bold text-slate-800">{avgRating}</p><p className="text-sm text-slate-500">Avg Rating</p></div></div>
        </CardContent></Card>
        <Card className="stat-card"><CardContent className="p-0">
          <div className="flex items-center gap-4"><div className="p-3 rounded-xl bg-orange-100"><Clock className="h-6 w-6 text-orange-700" /></div>
            <div><p className="text-2xl font-bold text-slate-800">{pendingHrCount}</p><p className="text-sm text-slate-500">Pending HR Approval</p></div></div>
        </CardContent></Card>
        <Card className="stat-card"><CardContent className="p-0">
          <div className="flex items-center gap-4"><div className="p-3 rounded-xl bg-green-100"><CheckCircle className="h-6 w-6 text-green-700" /></div>
            <div><p className="text-2xl font-bold text-slate-800">{approvedCount}</p><p className="text-sm text-slate-500">Approved</p></div></div>
        </CardContent></Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <Select value={filterMonth} onValueChange={setFilterMonth}>
          <SelectTrigger className="input-field w-36" data-testid="filter-month"><SelectValue placeholder="All Months" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all_months">All Months</SelectItem>
            {MONTHS.map((m, i) => <SelectItem key={`filter-month-${m}`} value={String(i+1)}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input type="number" value={filterYear} onChange={e => setFilterYear(e.target.value)}
          className="input-field w-24" placeholder="Year" data-testid="filter-year" />
        <Select value={filterDept} onValueChange={setFilterDept}>
          <SelectTrigger className="input-field w-44" data-testid="filter-dept"><SelectValue placeholder="All Departments" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Departments</SelectItem>
            {departments.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {(filterMonth || filterDept !== "all") && (
          <Button variant="ghost" size="sm" onClick={() => { setFilterMonth(""); setFilterDept("all"); }} className="text-slate-400">Clear</Button>
        )}
      </div>

      {/* Employee Trend Section */}
      <Card className="card-soft" data-testid="trend-section">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-primary" /> Performance Trend
            </CardTitle>
            <Select value={trendEmployee || ""} onValueChange={v => { if (v) fetchTrend(v); }}>
              <SelectTrigger className="input-field w-52" data-testid="trend-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
              <SelectContent>
                {employees.filter(e => e.is_active).map(e => (
                  <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {trendLoading ? (
            <div className="flex justify-center py-8"><div className="animate-spin h-6 w-6 border-4 border-primary border-t-transparent rounded-full"></div></div>
          ) : !trendData ? (
            <div className="text-center py-8 text-slate-400 text-sm">Select an employee to view their performance trend</div>
          ) : trendData.trend.length === 0 ? (
            <div className="text-center py-8 text-slate-400 text-sm">No approved reviews found for {trendData.employee_name}</div>
          ) : (
            <div className="space-y-6">
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase mb-3">Overall Rating vs Department Average</p>
                <OverallTrendChart data={trendData.trend} />
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase mb-3">Parameter Breakdown Over Time</p>
                <ParamBreakdownChart data={trendData.trend} />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reviews */}
      <Card className="card-soft" data-testid="reviews-list">
        <CardHeader><CardTitle className="font-heading text-lg">Reviews ({filtered.length})</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-slate-500">No performance reviews found. Create your first monthly review.</div>
          ) : (
            <div className="space-y-3">
              {filtered.map(review => {
                const expanded = expandedId === review.id;
                const empDeptId = getEmpDept(review.employee_id);
                return (
                  <div key={review.id} className="border border-slate-100 rounded-xl hover:shadow-sm transition-shadow" data-testid={`review-card-${review.id}`}>
                    <div className="flex items-center gap-4 p-4 cursor-pointer" onClick={() => setExpandedId(expanded ? null : review.id)}>
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                        <User className="h-5 w-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-slate-800 text-sm">{getEmpName(review.employee_id)}</h3>
                          {empDeptId && <span className="text-[10px] text-slate-400">{getDeptName(empDeptId)}</span>}
                        </div>
                        <p className="text-xs text-slate-500">{review.review_period}</p>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <div className="text-right">
                          <p className="text-lg font-bold text-slate-700">{review.overall_rating}/5</p>
                          <p className="text-[10px] text-slate-400">Overall</p>
                        </div>
                        <StatusBadge status={review.status} />
                        {expanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                      </div>
                    </div>

                    {expanded && (
                      <div className="px-4 pb-4 border-t border-slate-50 pt-3 space-y-3">
                        <RatingDisplay ratings={review.ratings} />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                          {review.achievements && <div><p className="text-[10px] font-semibold text-slate-400 uppercase mb-0.5">Achievements</p><p className="text-slate-600">{review.achievements}</p></div>}
                          {review.areas_of_improvement && <div><p className="text-[10px] font-semibold text-slate-400 uppercase mb-0.5">Areas to Improve</p><p className="text-slate-600">{review.areas_of_improvement}</p></div>}
                          {review.goals && <div><p className="text-[10px] font-semibold text-slate-400 uppercase mb-0.5">Goals</p><p className="text-slate-600">{review.goals}</p></div>}
                          {review.comments && <div><p className="text-[10px] font-semibold text-slate-400 uppercase mb-0.5">Comments</p><p className="text-slate-600">{review.comments}</p></div>}
                        </div>
                        <div className="flex items-center gap-4 text-[10px] text-slate-400">
                          <span>Reviewed by: {review.reviewer_name || "—"}</span>
                          {review.hr_reviewer_name && <span>HR: {review.hr_reviewer_name}</span>}
                          {review.hr_comments && <span>HR Comments: {review.hr_comments}</span>}
                        </div>
                        {/* HR Action Buttons */}
                        <div className="flex gap-2 pt-1 flex-wrap">
                          <Button size="sm" variant="outline" className="rounded-full text-primary border-primary/20 hover:bg-primary/5"
                            data-testid={`view-trend-${review.id}`}
                            onClick={(e) => { e.stopPropagation(); fetchTrend(review.employee_id); }}>
                            <BarChart3 className="h-3.5 w-3.5 mr-1" /> View Trend
                          </Button>
                          {isHrAdmin && review.status === "dept_head_reviewed" && (
                            <>
                              <Button size="sm" className="rounded-full bg-green-600 hover:bg-green-700 text-white"
                                data-testid={`approve-review-${review.id}`}
                                onClick={(e) => { e.stopPropagation(); setSelectedReview(review); setHrComments(""); setHrDialogOpen(true); }}>
                                <CheckCircle className="h-3.5 w-3.5 mr-1" /> Approve
                              </Button>
                              <Button size="sm" variant="outline" className="rounded-full border-red-200 text-red-600 hover:bg-red-50"
                                data-testid={`reject-review-${review.id}`}
                                onClick={(e) => { e.stopPropagation(); setSelectedReview(review); setHrComments(""); setHrDialogOpen(true); }}>
                                <XCircle className="h-3.5 w-3.5 mr-1" /> Reject
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* HR Approval Dialog */}
      <Dialog open={hrDialogOpen} onOpenChange={setHrDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">HR Review Action</DialogTitle>
            <DialogDescription>
              Review for {selectedReview ? getEmpName(selectedReview.employee_id) : ""} — {selectedReview?.review_period}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {selectedReview && <RatingDisplay ratings={selectedReview.ratings} />}
            <div className="space-y-1.5">
              <Label className="text-xs">HR Comments (optional)</Label>
              <Textarea value={hrComments} onChange={e => setHrComments(e.target.value)}
                placeholder="Add feedback or notes..." className="input-field min-h-[60px]" data-testid="hr-comments-input" />
            </div>
            <div className="flex gap-2">
              <Button className="flex-1 bg-green-600 hover:bg-green-700 text-white rounded-full"
                data-testid="confirm-approve-btn"
                onClick={() => handleHrAction("approve")}>
                <CheckCircle className="h-4 w-4 mr-1.5" /> Approve
              </Button>
              <Button variant="outline" className="flex-1 border-red-200 text-red-600 hover:bg-red-50 rounded-full"
                data-testid="confirm-reject-btn"
                onClick={() => handleHrAction("reject")}>
                <XCircle className="h-4 w-4 mr-1.5" /> Reject
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Performance;
